import React, { useCallback, useMemo, useState } from 'react';
import { Formik } from 'formik';
import { Button, Grid, LinearProgress } from '@material-ui/core';
import { useQuery } from '@apollo/client';
import { getOptions } from '@ourbranch/lookups';
import { affinityToStem } from '@ourbranch/policy-utils';

import { brandConfig } from 'brand-config';
import { CreditCardFeeDisclaimer } from 'common/components/payment/credit-card-fee-disclaimer';
import { Label } from 'core';
import { useToast } from 'core/components/toast';
import Field from 'core/components/form/form.v2';
import ValueField from 'core/components/value-field';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { Card } from 'core/components/card';
import { Tooltip } from 'core/components/tooltip';
import { currencyFormatter } from 'core/helpers/formatters';
import icon from 'core/assets/svg/alert-white-bold.svg';
import { LabelTooltip } from 'core/components/label-tooltip';
import { tooltipHoverTexts } from 'core/helpers/constants';
import { GET_SWEEP_ACCOUNT } from 'core/components/sweep-account/sweep-account.queries';
import { isSweepAccountsEnabled } from 'core/helpers/sweep-account';
import { paymentMethodFieldLoadingProps } from './manual-charge-form.helpers';
import { resolvePolicyStem } from './sweep-eligibility';
import { validationSchema, remainingBalanceValidationSchema } from './manual-charge-form.validation';
import useStyles from '../payment.styles';

const ManualChargeForm = () => {
  const {
    account: {
      fetchFullAccountAndPolicy,
      policies: { policy: policyInfo }
    }
  } = useStore();
  const {
    user: { username, affinityCode },
    canManuallyChargeFee,
    canManuallyChargePolicy,
    viewOnly,
    isInternalAgent
  } = useSession();
  const classes = useStyles();
  const toast = useToast();
  const {
    policy,
    runManualCharge,
    billingDetails: { allPaymentMethods },
    hasBillingHold,
    totalFeeCost,
    premium,
    surplusContribution,
    totalReceived
  } = policyInfo;

  // Sweep account payments (#33155) are available to an independent agent whose own
  // affinity stem matches the policy's, and only if their agency has a sweep account.
  // The brand/env flag comes first, matching the other call sites (account menu, /sweep-account
  // route, checkout). Without it this query fired wherever sweep is off and the lambda refused it.
  const userStem = affinityCode ? affinityToStem(affinityCode).toUpperCase() : null;
  const policyStem = useMemo(() => resolvePolicyStem(policyInfo, policy), [policyInfo, policy]);
  const sweepEligible =
    isSweepAccountsEnabled() && !isInternalAgent && !!userStem && !!policyStem && userStem === policyStem;
  const { data: sweepData, loading: sweepLoading } = useQuery(GET_SWEEP_ACCOUNT, {
    skip: !sweepEligible,
    fetchPolicy: 'no-cache'
  });
  const sweepAccount = sweepData?.getSweepAccount || null;

  const memberPaymentMethodOptions = allPaymentMethods
    ? allPaymentMethods.map((paymentMethod) => {
        const { id, brand, last4, bankName } = paymentMethod;
        const prefix = bankName?.toUpperCase() || brand?.toUpperCase();
        return { id, value: prefix ? `${prefix} ****${last4}` : `****${last4}` };
      })
    : [];

  // Sweep payment methods share the member dropdown, prefixed so staff can tell them apart.
  // Selecting one flips the charge to the agency's sweep account on submit.
  const sweepPaymentMethodIds = new Set((sweepAccount?.paymentMethods ?? []).map((pm) => pm.id));
  const sweepPaymentMethodOptions = (sweepAccount?.paymentMethods ?? []).map((pm) => {
    const prefix = pm.bankName?.toUpperCase() || pm.brand?.toUpperCase();
    return {
      id: pm.id,
      value: `Sweep account — ${prefix ? `${prefix} ` : ''}****${pm.last4}${pm.isDefault ? ' (default)' : ''}`
    };
  });
  const paymentMethodOptions = [...memberPaymentMethodOptions, ...sweepPaymentMethodOptions];

  // The sweep account loads via a no-cache query that re-fires on every page open, so its methods
  // land after the initial render. Until it resolves, the dropdown would show only the member
  // methods (or nothing) and appear to "drop" the sweep option — disable + flag the field while
  // loading so it reads as loading rather than empty/broken. (#34093)
  const paymentMethodsLoading = sweepEligible && sweepLoading;

  // Independent agent, eligible policy, but no sweep account set up yet → point them to setup.
  // Gated on !sweepLoading so the hint doesn't flash before an existing account resolves.
  const sweepSetupHint =
    sweepEligible && !sweepLoading && !sweepAccount ? (
      <Tooltip
        label="sweep account payments"
        text="Set up a sweep account in the account menu (top-right person icon)."
        className={classes.sweepAccountHint}
      />
    ) : null;

  const [loading, setLoading] = useState(false);
  const onSubmit = useCallback(
    async (values) => {
      setLoading(true);
      const accountId = policy.id.slice(0, 9);
      const isSweepPayment = sweepPaymentMethodIds.has(values.paymentMethod);
      try {
        const res = await runManualCharge(
          policy.id,
          accountId,
          values.paymentMethod,
          values.amount,
          `Manual payment made by ${username}. ${values.internalDescription.trim()}`,
          values.billType,
          undefined, // payAhead
          isSweepPayment, // useSweepAccount
          isSweepPayment ? values.paymentMethod : undefined // sweepPaymentMethodId
        );
        const toastTextSuccessMessage =
          res.data.runManualCharge.type === 'bank_account'
            ? `A manual payment for bank account ending in ${res.data.runManualCharge.last4} is pending. It could take up to 4-7 business days to show as paid.`
            : `A manual payment for ${res.data.runManualCharge.type} ending with ${res.data.runManualCharge.last4} was successful`;
        setLoading(false);
        if (res.data.runManualCharge.success) {
          toast.notify({
            type: 'success',
            message: toastTextSuccessMessage
          });
          fetchFullAccountAndPolicy(policy.id, accountId);
        } else {
          const errorMessage = res.data.runManualCharge.error?.includes('must be verified')
            ? `Unable to run this manual charge because this ACH account has not yet been verified. To verify it, go to the member's page and confirm the microdeposits with the member.`
            : res.data.runManualCharge.error || 'There was an error during the manual charge';

          toast.notify({
            type: 'error',
            message: errorMessage
          });
        }
      } catch (e) {
        setLoading(false);
        toast.notify({
          type: 'error',
          message: e.message || 'There was an error during the manual charge'
        });
      }
    },
    [runManualCharge, policy.id, toast, fetchFullAccountAndPolicy, username, sweepPaymentMethodIds]
  );

  const billTypes = canManuallyChargeFee
    ? getOptions('billTypes')
    : getOptions('billTypes').filter((billType) => billType.id !== 'fee');

  const totalPrice = premium + totalFeeCost + surplusContribution;
  const totalRemaining = totalPrice - totalReceived;

  // Remaining balance is set amount to be paid
  // all agents can do a manual charge with exception to viewOnly
  if (!viewOnly && !canManuallyChargePolicy) {
    return (
      <>
        <div className={classes.manualChargeContainer}>
          <Grid container alignItems="center" justifyContent="flex-start" className={classes.rowTitle}>
            <Grid item xs={6}>
              <Label type="coverageValue">Pay remaining balance in full</Label>
            </Grid>
          </Grid>
          {hasBillingHold && (
            <Grid item>
              <Card className={classes.smallContainer}>
                <Grid className={classes.centeredContentContainer}>
                  <img src={icon} className={classes.smallIcon} alt="Notification Icon" />
                  You must remove the payment hold before making a manual charge.
                </Grid>
              </Card>
            </Grid>
          )}
          {!hasBillingHold && brandConfig.supportedFees?.creditCard && (
            <Grid item>
              <Card className={classes.smallContainer}>
                <Grid className={classes.centeredContentContainer}>
                  <img src={icon} className={classes.smallIcon} alt="Notification Icon" />
                  <CreditCardFeeDisclaimer amount={totalRemaining} />
                </Grid>
              </Card>
            </Grid>
          )}
          <Formik
            initialValues={{
              paymentMethod: '',
              amount: totalRemaining,
              billType: 'Premium + Fee',
              internalDescription: ''
            }}
            validationSchema={remainingBalanceValidationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={onSubmit}
          >
            {({ handleSubmit }) => {
              return (
                <div className={classes.manualChargeForm}>
                  <Grid container key="row1" spacing={4}>
                    <Grid item xs={6}>
                      <LabelTooltip
                        label="Payment Method"
                        tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.paymentTabPaymentMethod }}
                      >
                        <Field
                          name="paymentMethod"
                          type="select"
                          xs={12}
                          mode="dark"
                          options={paymentMethodOptions}
                          {...paymentMethodFieldLoadingProps(paymentMethodsLoading)}
                          ignoreGlobalDisabledState
                        />
                      </LabelTooltip>
                      {sweepSetupHint && <div className={classes.sweepAccountHintRow}>{sweepSetupHint}</div>}
                    </Grid>
                    <Grid container xs={6}>
                      <ValueField
                        label="Pay total remaining balance"
                        name="amount"
                        mode="dark"
                        xs={12}
                        formatter={currencyFormatter}
                        value={totalRemaining}
                        showTooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.paymentTabRemainingBalance }}
                        className={classes.totalRemainingField}
                        ignoreGlobalDisabledState
                      />
                    </Grid>
                  </Grid>
                  <Grid container key="row2" spacing={4} className={classes.spaceBetweenRow}>
                    <Field
                      name="internalDescription"
                      type="string"
                      label="Description"
                      xs={8}
                      ignoreGlobalDisabledState
                    />
                    <Button
                      variant="contained"
                      color="secondary"
                      className={classes.submit}
                      onClick={handleSubmit}
                      disabled={loading || hasBillingHold}
                    >
                      Submit Payment
                    </Button>
                  </Grid>
                </div>
              );
            }}
          </Formik>
        </div>
        {loading && <LinearProgress color="secondary" className={classes.loadingBar} />}
      </>
    );
  }

  // Can set amount to be paid
  // Only agents with canManuallyCharge can do a manual charge with the option to set amount to be paid
  return (
    <>
      <div className={classes.manualChargeContainer}>
        <Grid container alignItems="center" justifyContent="flex-start" className={classes.rowTitle}>
          <Grid item xs={3}>
            <Label type="coverageValue">Run a manual charge</Label>
          </Grid>
        </Grid>
        {hasBillingHold && (
          <Grid item>
            <Card className={classes.smallContainer}>
              <Grid className={classes.centeredContentContainer}>
                <img src={icon} className={classes.smallIcon} alt="Notification Icon" />
                You must remove the payment hold before making a manual charge.
              </Grid>
            </Card>
          </Grid>
        )}
        {!hasBillingHold && brandConfig.supportedFees?.creditCard && (
          <Grid item>
            <Card className={classes.smallContainer}>
              <Grid className={classes.centeredContentContainer}>
                <img src={icon} className={classes.smallIcon} alt="Notification Icon" />
                <CreditCardFeeDisclaimer />
              </Grid>
            </Card>
          </Grid>
        )}
        <Formik
          initialValues={{ paymentMethod: '', amount: null, billType: '', internalDescription: '' }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={onSubmit}
        >
          {({ handleSubmit }) => {
            return (
              <div className={classes.manualChargeForm}>
                <Grid container key="row1" spacing={4}>
                  <Field
                    name="paymentMethod"
                    type="select"
                    label="Payment Method"
                    xs={4}
                    mode="dark"
                    options={paymentMethodOptions}
                    {...paymentMethodFieldLoadingProps(paymentMethodsLoading)}
                    ignoreGlobalDisabledState
                  />
                  <Field
                    name="amount"
                    type="numeric"
                    label="Amount"
                    xs={4}
                    format={{
                      thousandSeparator: true,
                      prefix: '$',
                      allowNegative: false,
                      fixedDecimalScale: true,
                      decimalScale: 2
                    }}
                    ignoreGlobalDisabledState
                  />
                  <Field
                    name="billType"
                    type="select"
                    label="Bill Type"
                    xs={4}
                    options={billTypes}
                    mode="dark"
                    ignoreGlobalDisabledState
                  />
                </Grid>
                {sweepSetupHint && (
                  <Grid container key="sweep-hint" className={classes.sweepAccountHintRow}>
                    {sweepSetupHint}
                  </Grid>
                )}
                <Grid container key="row2" spacing={4} className={classes.spaceBetweenRow}>
                  <Field
                    name="internalDescription"
                    type="string"
                    label="Description"
                    xs={8}
                    ignoreGlobalDisabledState
                  />
                  <Button
                    variant="contained"
                    color="secondary"
                    className={classes.submit}
                    onClick={handleSubmit}
                    disabled={loading || hasBillingHold}
                  >
                    Charge Member
                  </Button>
                </Grid>
              </div>
            );
          }}
        </Formik>
      </div>
      {loading && <LinearProgress color="secondary" className={classes.loadingBar} />}
    </>
  );
};

export default ManualChargeForm;
